/**
 * Event → concept mapping (Implementation Guide §23).
 *
 * Every game event type resolves to the concept that explains it, so the
 * live game, the drive tracker and the post-game recap all pull the same
 * curated text out of the knowledge base instead of duplicating it.
 */

export const EVENT_TYPES = Object.freeze({
  RUN: "RUN",
  PASS_COMPLETE: "PASS_COMPLETE",
  PASS_INCOMPLETE: "PASS_INCOMPLETE",
  FIRST_DOWN: "FIRST_DOWN",
  TOUCHDOWN: "TOUCHDOWN",
  FIELD_GOAL: "FIELD_GOAL",
  PUNT: "PUNT",
  INTERCEPTION: "INTERCEPTION",
  FUMBLE: "FUMBLE",
  SACK: "SACK",
  PENALTY: "PENALTY",
  KICKOFF: "KICKOFF",
  EXTRA_POINT: "EXTRA_POINT",
  TWO_POINT_CONVERSION: "TWO_POINT_CONVERSION",
  /* engine-generated, not part of the PRD's initial provider taxonomy */
  TURNOVER_ON_DOWNS: "TURNOVER_ON_DOWNS",
  FIELD_GOAL_MISSED: "FIELD_GOAL_MISSED",
  END_OF_QUARTER: "END_OF_QUARTER",
  END_OF_GAME: "END_OF_GAME",
});

export const EVENT_CONCEPT_MAP = Object.freeze({
  [EVENT_TYPES.RUN]: "running-back",
  [EVENT_TYPES.PASS_COMPLETE]: "wide-receiver",
  [EVENT_TYPES.PASS_INCOMPLETE]: "incomplete-pass",
  [EVENT_TYPES.FIRST_DOWN]: "first-down",
  [EVENT_TYPES.TOUCHDOWN]: "touchdown",
  [EVENT_TYPES.FIELD_GOAL]: "field-goal",
  [EVENT_TYPES.FIELD_GOAL_MISSED]: "field-goal",
  [EVENT_TYPES.PUNT]: "punt",
  [EVENT_TYPES.INTERCEPTION]: "interception",
  [EVENT_TYPES.FUMBLE]: "fumble",
  [EVENT_TYPES.SACK]: "sack",
  [EVENT_TYPES.PENALTY]: "penalty",
  [EVENT_TYPES.KICKOFF]: "kickoff",
  [EVENT_TYPES.EXTRA_POINT]: "extra-point",
  [EVENT_TYPES.TWO_POINT_CONVERSION]: "two-point-conversion",
  [EVENT_TYPES.TURNOVER_ON_DOWNS]: "fourth-down",
  [EVENT_TYPES.END_OF_QUARTER]: "clock",
  [EVENT_TYPES.END_OF_GAME]: "clock",
});

/** Short, friendly labels for the play feed. */
export const EVENT_LABELS = Object.freeze({
  [EVENT_TYPES.RUN]: "Run",
  [EVENT_TYPES.PASS_COMPLETE]: "Pass",
  [EVENT_TYPES.PASS_INCOMPLETE]: "Incomplete",
  [EVENT_TYPES.FIRST_DOWN]: "First down",
  [EVENT_TYPES.TOUCHDOWN]: "Touchdown",
  [EVENT_TYPES.FIELD_GOAL]: "Field goal",
  [EVENT_TYPES.FIELD_GOAL_MISSED]: "Missed field goal",
  [EVENT_TYPES.PUNT]: "Punt",
  [EVENT_TYPES.INTERCEPTION]: "Interception",
  [EVENT_TYPES.FUMBLE]: "Fumble",
  [EVENT_TYPES.SACK]: "Sack",
  [EVENT_TYPES.PENALTY]: "Penalty",
  [EVENT_TYPES.KICKOFF]: "Kickoff",
  [EVENT_TYPES.EXTRA_POINT]: "Extra point",
  [EVENT_TYPES.TWO_POINT_CONVERSION]: "Two-point try",
  [EVENT_TYPES.TURNOVER_ON_DOWNS]: "Turnover on downs",
  [EVENT_TYPES.END_OF_QUARTER]: "End of quarter",
  [EVENT_TYPES.END_OF_GAME]: "Final",
});

/** Events worth interrupting the viewer for. */
export const BIG_MOMENT_EVENTS = Object.freeze([
  EVENT_TYPES.TOUCHDOWN,
  EVENT_TYPES.FIELD_GOAL,
  EVENT_TYPES.INTERCEPTION,
  EVENT_TYPES.FUMBLE,
  EVENT_TYPES.SACK,
  EVENT_TYPES.PUNT,
  EVENT_TYPES.TURNOVER_ON_DOWNS,
  EVENT_TYPES.TWO_POINT_CONVERSION,
]);

export function conceptIdForEvent(eventType) {
  return EVENT_CONCEPT_MAP[eventType] ?? null;
}

export function labelForEvent(eventType) {
  return EVENT_LABELS[eventType] ?? "Play";
}
